'use client';

import { useState, useEffect } from 'react';
import { AboutUsHeader } from '@/features/about-us/components/AboutUsHeader';
import { cn } from '@/shadcn/lib/utils';
import { AboutUsButton } from '@/features/about-us/components/AboutUsButton';
import { RoadmapSideContent } from '@/features/about-us/components/RoadmapSideContent';

const B = '/images/about-us/business';

const PARTNERSHIPS = [
    { icon: `${B}/biznis_ico_makleri.svg`, title: 'Makléri', subtitle: 'a realitné kancelárie' },
    {
        icon: `${B}/biznis_ico_developerske_spolocnosti.svg`,
        title: 'Developerské',
        subtitle: 'spoločnosti',
    },
    { icon: `${B}/biznis_ico_hypo_specialisti.svg`, title: 'Hypotekárni', subtitle: 'špecialisti' },
    { icon: `${B}/biznis_ico_banky.svg`, title: 'Banky', subtitle: 'a finančné inštitúcie' },
];

const ALL_IN_ONE_FEATURES = [
    {
        icon: `${B}/biznis_ico_ai_first.svg`,
        title: 'AI-first realitná platforma',
        body: 'Systém samostatne triedi a vyhodnocuje realitné dáta, čím nahrádza manuálnu prácu a radikálne znižuje chybovosť procesov.',
    },
    {
        icon: `${B}/biznis_ico_algorytm_parovanie.svg`,
        title: 'Algoritmické párovanie dopytu',
        body: 'Technológia prepája nehnuteľnosti s relevantnými záujemcami. Partneri získavajú kvalitnejšie leady, investori vyššiu efektivitu.',
    },
    {
        icon: `${B}/biznis_workflow_sloboda.svg`,
        title: 'Kompletný workflow v jedinom rozhraní',
        body: 'Celý predaj prebieha digitálne v jednom bezpečnom prostredí. Od kontaktu po uzavretie bez zbytočnej administratívy.',
    },
    {
        icon: `${B}/biznis_ico_inovacne_jadro.svg`,
        title: 'Inovačné jadro Realico X',
        body: 'Špeciálny priestor, kde transformujeme odvážne technologické vízie na funkčné nástroje plne pripravené pre náročný realitný trh.',
    },
    {
        icon: `${B}/biznis_ico_individual_rozhranie.svg`,
        title: 'Individuálne rozhranie pre každého užívateľa',
        body: 'Platforma sa priebežne učí z vašich preferencií a automaticky zobrazuje len ten najrelevantnejší obsah pre vašu potrebu.',
    },
    {
        icon: `${B}/biznis_ico_pripravenost.svg`,
        title: 'Pripravenosť na okamžitý rast',
        body: 'Moderné technické zázemie umožňuje hladké prepojenie s externými partnermi a plynulú expanziu na nové zahraničné trhy.',
    },
];

const ROADMAP = [
    {
        side: 'right',
        icon: `${B}/invest_ico_rozvoj_ai_funkcii.svg`,
        title: 'Rozvoj kľúčových\nAI funkcií',
        body: 'Rozšírenie inteligent funkcií ako sú prediktívne hľadanie a AI asistent v reáľnom čase.',
    },
    {
        side: 'left',
        icon: `${B}/invest_ico_rast_uzivatelskej_zakladne.svg`,
        title: 'Rast používateľskej\nzákladne',
        body: 'Aktívna nabore klientov v všetkých regiónoch a úspešná integrácia v krajinách Stredom Východe.',
    },
    {
        side: 'right',
        icon: `${B}/invest_ico_nove_inzertne_modely.svg`,
        title: 'Nové inzertné\na partnerské modely',
        body: 'Spustenie nových biznis modelov a unikátnych spôsobov spolupráce s partnermi v ekosystéme.',
    },
    {
        side: 'left',
        icon: `${B}/invest_ico_expanzia_cee.svg`,
        title: 'Expanzia CEE',
        body: 'Vstúpiť na Český a Maďarský trh s plne lokalizovaným riešením.',
    },
] as const;

export default function BusinessWithRealico() {
    const [lineH, setLineH] = useState(1000);

    useEffect(() => {
        const updateLineH = () => {
            setLineH(window.innerWidth >= 1200 ? 1100 : 1000);
        };

        updateLineH();

        window.addEventListener('resize', updateLineH);

        return () => window.removeEventListener('resize', updateLineH);
    }, []);

    return (
        <div className="bg-white">
            <section
                className={cn('relative hero-flat bg-gradient-to-b from-[#e0aa03] to-[#f3c300]')}
            >
                <div
                    aria-hidden
                    className="absolute inset-0 z-0 pointer-events-none"
                    style={{
                        backgroundImage: `url(${B}/biznis_hero_bgr_icons.png)`,
                        backgroundRepeat: 'no-repeat',
                        backgroundPosition: 'center center',
                        backgroundSize: '100% auto',
                    }}
                />
                <div className="cust-container">
                    {/* Header */}
                    <div className="py-[15px]">
                        <AboutUsHeader />
                    </div>
                    <div className="inset-0 z-10 flex items-center pt-[40px] xl:pt-[55px] xl:pt-[70px]">
                        <div className="mx-auto w-full grid md:grid-cols-[55%_45%] gap-10 items-center">
                            <div className="relative w-full">
                                <h1 className="font-display leading-[1.02] tracking-[-0.04em] font-bold text-white text-[40px] md:text-[45px] xl:text-[50px]">
                                    Tvorte s nami.
                                </h1>
                                <p className="mt-5 text-[#191919] leading-6 text-[18px] font-medium md:text-[20px] xl:text-[22px]">
                                    Realico mení pravidlá realitného vyhľadávania.
                                </p>
                                <p className="mt-4 text-[15px] text-[#191919]/80 leading-[135%] max-w-[600px] xl:text-[16px]">
                                    Naša AI-first platforma zvyšuje efektivitu používateľov, prináša
                                    viac možností realitným partnerom a vytvára jasnú rastovú
                                    príležitosť pre investorov, ktorí chcú byť súčasťou budúcnosti
                                    trhu.
                                </p>
                                <AboutUsButton className="mb-[100px] md:mb-[130px] xl:mb-[150px]">
                                    Viac informácií
                                </AboutUsButton>
                            </div>
                            <div />
                        </div>
                    </div>
                </div>

                <div className="relative cust-container">
                    <img
                        src={`${B}/biznis_hero_img.png`}
                        alt=""
                        className={cn(
                            'absolute max-w-[235px] right-[-2%] bottom-[0] sm:right-[2%] md:right-[0] md:max-w-[380px] lg:max-w-[430px] xl:max-w-[600px] xl:right-[0%] 2xl:max-w-[680px] 2xl:right-[-5%]',
                            '',
                        )}
                    />
                    <img
                        src={`${B}/biznis_hero_ico_moneysack.png`}
                        alt=""
                        aria-hidden
                        className={cn(
                            'absolute left-[7%] z-20 pointer-events-none select-none bottom-0 translate-y-[35%] w-[60px] md:w-[80px] xl:w-[100px] xl:left-[7%]',
                        )}
                    />
                </div>
            </section>

            {/* Realico all-in-one features */}
            <section className="bg-white">
                <div className="cust-container">
                    <div>
                        <div className="pt-[110px] pb-[40px] md:pt-[120px] md:pb-[50px] xl:pt-[160px] xl:pb-[70px]">
                            <h2 className="font-display text-center text-[#191919] tracking-[-0.04em] font-bold text-[35px] leading-[1.2] md:text-[40px] xl:text-[45px]">
                                Realico – vaše realitné all-in-one
                                <span className="text-hero">:</span>
                            </h2>
                            <div className="mt-[60px] grid grid-cols-1 md:grid-cols-3 md:mt-[90px] xl:mt-[140px]">
                                {ALL_IN_ONE_FEATURES.map((f, i) => {
                                    const col = i % 3;
                                    const row = Math.floor(i / 3);
                                    const isLast = i === ALL_IN_ONE_FEATURES.length - 1;
                                    return (
                                        <div
                                            key={f.title}
                                            className={cn(
                                                'flex flex-col items-center text-center border-b py-[30px] px-[20px] xl:py-[35px] xl:px-[25px]',
                                                isLast && 'border-b-0',
                                                row !== 0 && 'md:border-b-0',
                                                col !== 2 && 'md:border-r',
                                            )}
                                        >
                                            <img
                                                className="h-[120px] w-auto md:h-[100px] xl:h-[130px]"
                                                src={f.icon}
                                                alt=""
                                            />
                                            <div className="w-full max-w-[400px]">
                                                <h3 className="font-display font-bold text-black leading-[1.2] mt-[32px] text-[21px] md:text-[19px] xl:text-[22px]">
                                                    {f.title}
                                                </h3>
                                                <p
                                                    className="text-[#4b5563] leading-[1.6] text-[13px] mt-[20px] xl:text-[14px]"
                                                    style={{}}
                                                >
                                                    {f.body}
                                                </p>
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* Ready for partnership ? */}
            <section className="bg-white pt-[80px] pb-16 md:pt-[110px] xl:pt-[140px]">
                <div className="mx-auto cust-container">
                    <h2 className="font-display font-[700] text-center tracking-[-0.04em] leading-[120%] text-[35px] md:text-[40px] xl:text-[45px]">
                        Pripravení na partnerstvo<span className="text-hero">?</span>
                    </h2>
                    <p className="text-center mx-auto mt-[30px] leading-[145%] text-[15px] xl:text-[16px] max-w-[1100px]">
                        Naše riešenia sme vyvinuli pre kľúčových hráčov, ktorí tvoria trh. Či už ste
                        progresívna realitná kancelária, developer, hypotekárny špecialista alebo
                        finančná inštitúcia, Realico vám poskytne technológie pre novú éru biznisu.
                    </p>

                    <div className="mt-24 grid grid-cols-2 md:grid-cols-4 gap-y-12 max-w-6xl mx-auto">
                        {PARTNERSHIPS.map((p, i) => (
                            <div
                                key={p.title}
                                className="flex flex-col items-center text-center relative px-4"
                            >
                                {i !== 0 && (
                                    <span
                                        aria-hidden
                                        className="hidden md:block absolute left-0 w-px h-[70px] md:-bottom-[11px] xl:h-[80px] xl:-bottom-[13x]"
                                        style={{
                                            backgroundColor: '#f7e59c',
                                        }}
                                    />
                                )}
                                <div className="relative w-[140px] h-[140px] flex items-center justify-center xl:w-[200px] xl:w-[200px]">
                                    <div
                                        className="absolute inset-0 rounded-full"
                                        style={{
                                            background:
                                                'conic-gradient(from 270deg,#f3c300 0deg,rgba(243,195,0,0) 359deg,#fff 360deg)',

                                            WebkitMask:
                                                'radial-gradient(circle, transparent 0 42%, #000 43% 50%, transparent 51%)',

                                            mask: 'radial-gradient(circle, transparent 0 42%, #000 43% 50%, transparent 51%)',
                                        }}
                                    />
                                    <img
                                        src={p.icon}
                                        alt=""
                                        className="relative object-contain w-[35px]"
                                    />
                                </div>
                                <h3 className=" font-display text-[18px] font-bold leading-tight md:mt-[10px] xl:text-[20px] xl:mt-[20px]">
                                    {p.title}
                                </h3>
                                <div className="text-hero text-[16px] font-bold leading-tight mt-1 xl:text-[18px]">
                                    <p className="text-cust-primary">{p.subtitle}</p>
                                </div>
                            </div>
                        ))}
                    </div>

                    <div className="mt-24" style={{ height: '1px', backgroundColor: '#d6d6d6' }} />
                </div>
            </section>

            {/* Roadmap */}
            <section className="pt-[40px]">
                <div className="cust-container">
                    <h2 className="font-display text-center text-[#191919] tracking-[-0.05em] leading-[1.2] font-[700] text-[35px] md:text-[40px] xl:text-[45px]">
                        Kam Realico smeruje<span className="text-hero">?</span>
                    </h2>
                    <p className="text-center text-[15px] text-[#191919]/70 mt-4 max-w-[800px] mx-auto xl:text-[16px]">
                        Naša cesta smeruje k inteligentnejším riešeniam, širšej komunite a expanzii
                        do strednej Európy — presne tam, kde sa stretáva inovácia s rastom.
                    </p>

                    {(() => {
                        const LINE_H = lineH;
                        const LINE_W = 160;
                        const dotY = [10.52, 36.46, 62.29, 88.23];
                        return (
                            <div
                                className="mt-20 relative mx-auto max-w-[1100px]"
                                style={{ height: `${LINE_H}px` }}
                            >
                                <img
                                    src={`${B}/invest_kam_smeruje_line.png`}
                                    alt=""
                                    aria-hidden
                                    className="pointer-events-none select-none absolute left-1/2 top-0 -translate-x-1/2"
                                    style={{ width: `${LINE_W}px`, height: `${LINE_H}px` }}
                                />
                                {ROADMAP.map((r, i) => {
                                    const topPx = (dotY[i] / 100) * LINE_H;
                                    const isRight = r.side === 'right';
                                    return (
                                        <div
                                            key={i}
                                            className="absolute left-0 right-0 min-h-[0px]"
                                            style={{
                                                top: `${topPx}px`,
                                                transform: 'translateY(-50%)',
                                            }}
                                        >
                                            <div className="grid grid-cols-2 items-start min-h-[0px]">
                                                <RoadmapSideContent
                                                    index={i}
                                                    side={r.side}
                                                    title={r.title}
                                                    body={r.body}
                                                    icon={r.icon}
                                                />
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>
                        );
                    })()}
                </div>
            </section>

            {/* CTA */}
            <section className="py-[100px]  text-center md:py-[120px] xl:py-[140px]">
                <div className="mx-auto px-6 mx-w-[600px] xl:max-w-[1100px]">
                    <h3 className="font-display font-bold text-[#191919] tracking-[-0.03em] leading-[1.2] text-[35px] md:text-[40px] xl:text-[45px]">
                        Staňte sa súčasťou našej cesty
                    </h3>
                    <p className="text-[#191919]/80 mx-auto leading-[1.7] mt-[30px] text-[15px] max-w-[900px] xl:text-[16px] xl:mt-[35px] ">
                        Ponúkame flexibilné investičné príležitosti pre všetkých, ktorí chcú s nami
                        tvoriť inovatívnu budúcnosť realitného trhu.
                        <br />
                        Ak máte záujem o investičný vstup do Realico, zanechajte nám svoj e-mail a
                        získajte podrobné investičné materiály.
                    </p>
                    <div className="flex justify-center" style={{ marginTop: '48px' }}>
                        <AboutUsButton>Kontaktuje nás</AboutUsButton>
                    </div>
                </div>
            </section>
        </div>
    );
}
